import { Injectable } from "@angular/core";
import { MatLegacyDialog as MatDialog } from "@angular/material/legacy-dialog";
import { ActivatedRouteSnapshot, CanActivate, RouterStateSnapshot } from "@angular/router";
import { Store } from "@ngrx/store";
import { OpenIdConnectSelector } from "@obg/common.verification";
import { batchActions } from "@obg/ngrx-utils";
import { MarketingSettingsAction } from "@obg/ngx.common";
import { CORE_ROUTE, CoreState, CustomerManagementSelector, SessionSelector } from "@obg/ngx.core";
import { DeviceConfigService } from "@obg/ngx.global";
import { RouteAction, RouteSelector, RouteService } from "@obg/ngx.router";
import { FUNDS_PROTECTION_DIALOG_ID } from "@obg/ngx.ui.common";
import { ignoreNil } from "@obg/rxjs";
import { combineLatest, EMPTY, merge, Observable, Subscription } from "rxjs";
import { filter, map, switchMap, take, tap } from "rxjs/operators";

import { FirstLoginService } from "../first-login/first-login.service";
import { LoginFlowService } from "../flow/login-flow.service";
import { LoginUiFlowService } from "../login-ui/login-ui-flow.service";

import { CONSENTS_ROUTES, RESET_PASSWORD_ROUTES } from "./post-login-flow.const";

@Injectable({
	providedIn: "root",
})
export class PostLoginFlowInitGuard implements CanActivate {
	private isMobile = !this.deviceConfigService.isDesktop();
	private postLoginFlow$$: Subscription | undefined;
	private initializeWelcomeFlow$$: Subscription | undefined;
	private hasAcceptedDepositTermsAndConditions$$: Subscription | undefined;

	constructor(
		private store: Store<CoreState>,
		private loginFlowService: LoginFlowService,
		private firstLoginService: FirstLoginService,
		private loginUiFlowService: LoginUiFlowService,
		private sessionSelector: SessionSelector,
		private marketingSettingsAction: MarketingSettingsAction,
		private routeService: RouteService,
		private deviceConfigService: DeviceConfigService,
		private matDialog: MatDialog,
		private oidcSelector: OpenIdConnectSelector
	) {}

	canActivate(_route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
		this.unsubs();

		const route = this.routeService.getConfigFromStateSnapshot(state);
		const isQuickRegisterRoute = route?.name === CORE_ROUTE.registration.quickRegister;
		const isTermsAndConditionsRoute = route?.name === CORE_ROUTE.termsAndConditions;

		if (!this.hasAcceptedDepositTermsAndConditions$$) {
			this.hasAcceptedDepositTermsAndConditions$$ = this.store
				.select(CustomerManagementSelector.hasAcceptedDepositTermsAndConditions)
				.pipe(
					filter(
						(hasAcceptedDepositTermsAndConditions) =>
							isTermsAndConditionsRoute && hasAcceptedDepositTermsAndConditions === false
					),
					tap(() => {
						const dialog = this.matDialog.getDialogById(FUNDS_PROTECTION_DIALOG_ID);
						dialog?.close();
					})
				)
				.subscribe();
		}

		return combineLatest([
			this.store.select(this.sessionSelector.isAuthenticated()),
			this.store.select(this.oidcSelector.getToken()),
			this.store.select(this.sessionSelector.getTermsAndConditionsToken()),
		]).pipe(
			map(([isAuthenticated, oidcToken, termsAndConditionsToken]) => {
				if (isAuthenticated && !isTermsAndConditionsRoute && !oidcToken) {
					this.store.dispatch(RouteAction.navigate(CORE_ROUTE.home));
					return false;
				}

				if (!this.postLoginFlow$$) {
					const shouldInitAuthHandler =
						!isQuickRegisterRoute && !(isTermsAndConditionsRoute && !termsAndConditionsToken);

					const authHandler$ =
						this.isMobile && shouldInitAuthHandler
							? this.store.select(RouteSelector.getCurrent).pipe(
									take(1),
									map((current) => RESET_PASSWORD_ROUTES.includes(current?.name ?? "")),
									switchMap((isResetPasswordRoute) =>
										this.loginFlowService.authHandler$(!isResetPasswordRoute)
									)
								)
							: EMPTY;

					const clearConsents$ = this.isMobile
						? this.store.select(RouteSelector.getCurrent).pipe(
								ignoreNil(),
								take(1),
								filter((current) => !CONSENTS_ROUTES.includes(current.name)),
								tap(() =>
									this.store.dispatch(
										batchActions(
											this.marketingSettingsAction.clearMissingConsents(),
											this.marketingSettingsAction.clearMissingMarketingSettingsDetails()
										)
									)
								)
							)
						: EMPTY;

					this.postLoginFlow$$ = merge(
						clearConsents$,
						authHandler$,
						this.loginUiFlowService.authorize$()
					).subscribe();
				}

				if (!this.initializeWelcomeFlow$$) {
					this.initializeWelcomeFlow$$ = this.firstLoginService.initializeWelcomeFlow$().subscribe();
				}

				return true;
			})
		);
	}

	private unsubs() {
		this.postLoginFlow$$?.unsubscribe();
		this.initializeWelcomeFlow$$?.unsubscribe();
		this.hasAcceptedDepositTermsAndConditions$$?.unsubscribe();
		this.postLoginFlow$$ = undefined;
		this.initializeWelcomeFlow$$ = undefined;
		this.hasAcceptedDepositTermsAndConditions$$ = undefined;
	}
}
